import { useEffect, useMemo, useState } from "react";
import {
  type UseFormReturn,
  useForm,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { PlusIcon, Trash2Icon } from "lucide-react";
import { type ColumnDef } from "@tanstack/react-table";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DataTable } from "@/components/data-table";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EntitySheet } from "@/components/entity-sheet";
import { TagsMultiSelect } from "../../tags-multi-select";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — legacy JS, no type defs.
import DynamicFieldSelectorDialog from "../../dynamic-field-selector";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — legacy JS, no type defs.
import DynamicFieldValueComponent from "../../dynamic-field-value";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — legacy JS, no type defs.
import ItemFlowDialog from "../item-flow-dialog";
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-expect-error — legacy JS, no type defs.
import BFHierarchyTable from "../../hierarchy-table";
import { useNotificationsProvider } from "../../../context/NotificationsContext";
import { Permissions } from "../../../entities/permissions";
import { hasPermission } from "../../../pages/login/selectors";
import { FlowTypes } from "../../../entities/flow-type";
import { Features } from "../../../entities/features";
import {
  useEditFlowMutation,
  useFlowTypeListQuery,
  useGetTagsForEntityQuery,
  useIsFeatureEnabled,
  useManageItemsFlowMutation,
  useSetTagsForEntityMutation,
} from "../../../services/api";

interface TagRef {
  id: number;
  name: string;
  color: string | null;
}

interface FlowItem {
  id: string | number;
  title?: string;
  children?: FlowItem[];
  [key: string]: unknown;
}

interface FlowType {
  id: string | number;
  name?: string;
  translatedName?: string;
}

export interface FlowRowForSheet {
  id: string | number;
  name?: string;
  flowType?: FlowType;
  hasSignatures?: boolean;
  signatureTitles?: string[];
  isEvaluationFormActive?: boolean;
  evaluationFormDynamicFields?: Array<{ title: string; dataType: string; value?: unknown }>;
  items?: FlowItem[];
  tags?: TagRef[];
  [key: string]: unknown;
}

interface FlowSheetProps {
  value: FlowRowForSheet | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  canEdit: boolean;
  initialEditing?: boolean;
  onEdited?: () => void;
  onDelete?: () => void;
}

/*
 * Read+edit panel for a single Flow. Replaces three legacy dialogs:
 *   - EditFlowDialog (form: name + type + signatures + eval form + tags)
 *   - SeeFlowDialog  (items table + manage button)
 *   - The "see" half of those, folded into the sheet's read mode.
 *
 * Items management still pops the MUI ItemFlowDialog sub-modal for the
 * "Add items" affordance — that hierarchy picker is a Phase 6 cleanup.
 */
export function FlowSheet({
  value,
  open,
  onOpenChange,
  canEdit,
  initialEditing = false,
  onEdited,
  onDelete,
}: FlowSheetProps) {
  const { t } = useTranslation();
  const { showNotification } = useNotificationsProvider();

  const { data: flowTypes } = useFlowTypeListQuery();
  const [editFlow, { isLoading: isEditing }] = useEditFlowMutation();
  const [setTags, { isLoading: isSettingTags }] = useSetTagsForEntityMutation();
  const [manageItemsFlow, { isLoading: isManagingItems }] =
    useManageItemsFlowMutation();

  const isSignatureFeatureEnabled = useIsFeatureEnabled(Features.FT_INSPECTION_SIGNATURES);
  const isEvalFormFeatureEnabled = useIsFeatureEnabled(Features.FT_RESOURCE_EVALUATION_FORM);

  const { data: currentTags } = useGetTagsForEntityQuery(
    { taggableType: "Flow", taggableId: value?.id },
    { skip: !open || !value?.id },
  ) as { data?: TagRef[] };

  const hasAddItemPermission = useSelector(hasPermission(Permissions.PAGE_FLOWS_ADD_ITEM));
  const hasRemoveItemPermission = useSelector(
    hasPermission(Permissions.PAGE_FLOWS_REMOVE_ITEM),
  );

  const [editing, setEditing] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "items">("details");

  const [openDynamicFieldSelector, setOpenDynamicFieldSelector] = useState(false);
  const [selectedDynamicField, setSelectedDynamicField] = useState<{
    title: string;
    dataType: string;
    value?: unknown;
  } | null>(null);

  // Items tab state — kept outside the form because the Items tab has
  // its own save flow (manageItemsFlow mutation) independent of the
  // details Save button.
  const [items, setItems] = useState<FlowItem[]>([]);
  const [openItemFlowDialog, setOpenItemFlowDialog] = useState(false);

  const schema = useMemo(
    () =>
      z
        .object({
          flowTitle: z.string().trim().min(1, t("common.required")),
          typeId: z.string().min(1, t("common.required")),
          selectedTags: z.array(z.any()),
          hasSignatures: z.boolean(),
          signaturesCount: z.union([z.literal(1), z.literal(2)]),
          signatureOneTitle: z.string(),
          signatureTwoTitle: z.string(),
          isEvalFormActive: z.boolean(),
          dynamicFields: z.array(z.any()),
        })
        .superRefine((v, ctx) => {
          if (v.hasSignatures) {
            if (v.signatureOneTitle.length === 0) {
              ctx.addIssue({
                code: "custom",
                path: ["signatureOneTitle"],
                message: t("flows.add-flow-dialog.signature-title-missing"),
              });
            }
            if (v.signaturesCount === 2 && v.signatureTwoTitle.length === 0) {
              ctx.addIssue({
                code: "custom",
                path: ["signatureTwoTitle"],
                message: t("flows.add-flow-dialog.signature-titles-missing"),
              });
            }
          }
        }),
    [t],
  );

  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      flowTitle: "",
      typeId: "",
      selectedTags: [],
      hasSignatures: false,
      signaturesCount: 1,
      signatureOneTitle: "",
      signatureTwoTitle: "",
      isEvalFormActive: false,
      dynamicFields: [],
    },
  });

  // Re-seed every time the upstream row changes or the sheet (re)opens.
  useEffect(() => {
    if (!value) return;
    form.reset({
      flowTitle: value.name ?? "",
      typeId: value.flowType?.id != null ? String(value.flowType.id) : "",
      selectedTags: value.tags ?? [],
      hasSignatures: Boolean(value.hasSignatures),
      signaturesCount:
        value.signatureTitles && value.signatureTitles.length === 2 ? 2 : 1,
      signatureOneTitle: value.signatureTitles?.[0] ?? "",
      signatureTwoTitle: value.signatureTitles?.[1] ?? "",
      isEvalFormActive: Boolean(value.isEvaluationFormActive),
      dynamicFields: value.evaluationFormDynamicFields ?? [],
    });
    setItems(value.items ? [...value.items] : []);
    setEditing(open ? initialEditing : false);
    setActiveTab("details");
    // `form` is intentionally excluded: including it would cause a reset loop
    // because form.reset() itself triggers re-renders. Re-seed only when the
    // displayed entity changes or the sheet opens/closes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value?.id, open, initialEditing]);

  useEffect(() => {
    if (Array.isArray(currentTags)) {
      form.setValue(
        "selectedTags",
        currentTags.map((tag) => ({
          id: tag.id,
          name: tag.name,
          color: tag.color,
        })),
      );
    } else if (value?.tags) {
      form.setValue("selectedTags", value.tags);
    }
    // `form` is intentionally excluded — same reset-loop reason as above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTags, value?.tags]);

  const flowTypesList = useMemo(() => {
    type FlowTypeOption = { id: string | number; name?: string; title: string };
    interface FlowTypeRow { id: string | number; name?: string }
    interface FlowTypesPayload { flowTypes?: FlowTypeRow[] }
    const list = ((flowTypes as FlowTypesPayload | undefined)?.flowTypes ?? []) as FlowTypeRow[];
    return list.map((flowType): FlowTypeOption => {
      if (flowType.name === FlowTypes.FREE) {
        return {
          id: flowType.id,
          name: flowType.name,
          title: t("flows.add-flow-dialog.select-type.free"),
        };
      }
      if (flowType.name === FlowTypes.SEQUENTIAL) {
        return {
          id: flowType.id,
          name: flowType.name,
          title: t("flows.add-flow-dialog.select-type.sequencial"),
        };
      }
      return {
        id: flowType.id,
        name: flowType.name,
        title: flowType.name ?? String(flowType.id),
      };
    });
  }, [flowTypes, t]);

  const handleSave = form.handleSubmit(async (values) => {
    if (!value) return;

    const signatureTitles: string[] = values.hasSignatures
      ? values.signaturesCount === 1
        ? [values.signatureOneTitle]
        : [values.signatureOneTitle, values.signatureTwoTitle]
      : [];

    const data: Record<string, unknown> = {
      id: value.id,
      name: values.flowTitle.trim(),
      flowTypeId: values.typeId,
      hasSignatures: values.hasSignatures,
      isResourceEvaluationFormActive: values.isEvalFormActive,
    };
    if (values.hasSignatures) data.signatureTitles = signatureTitles;
    if (values.isEvalFormActive) {
      data.evaluationFormDynamicFields = values.dynamicFields;
    }

    try {
      await editFlow(data).unwrap();
      await setTags({
        taggableType: "Flow",
        taggableId: value.id,
        tagIds: values.selectedTags.map((tag) => tag.id),
      }).unwrap();
      setEditing(false);
      onEdited?.();
    } catch (err) {
      const errorCode = (err as { data?: { validationErrors?: { errorCode?: string }[] } })
        ?.data?.validationErrors?.[0]?.errorCode;
      if (errorCode === "FLOW_DETAILS_ALREADY_USED") {
        showNotification("error", t("flows.edit-flow-dialog.already-exists-error"));
      } else {
        showNotification("error", t("common.api-error"));
      }
    }
  });

  async function handleSaveItems() {
    if (!value) return;
    try {
      const allIds = collectAllIds(items);
      await manageItemsFlow({ id: value.id, items: allIds }).unwrap();
      onEdited?.();
    } catch {
      showNotification("error", t("common.api-error"));
    }
  }

  function onAddItems(newItems: FlowItem[]) {
    setItems(newItems);
  }

  function onRemoveItem(row: { value?: { id: string | number }; rowIndex?: number }) {
    if (!row) return;
    const targetId =
      value?.flowType?.name === FlowTypes.SEQUENTIAL && row.rowIndex != null
        ? items[row.rowIndex].id
        : row.value?.id;
    if (targetId == null) return;
    setItems((prev) => removeItemById(prev, "children", targetId));
  }

  function onAddDynamicField(data: { title: string; dataType: string; value?: unknown }) {
    setOpenDynamicFieldSelector(false);
    const current = form.getValues("dynamicFields");
    if (selectedDynamicField) {
      form.setValue(
        "dynamicFields",
        current.map((f) => (f === selectedDynamicField ? data : f)),
      );
      setSelectedDynamicField(null);
      return;
    }
    form.setValue("dynamicFields", [...current, data]);
  }

  function onEditDynamicField(data: { title: string; dataType: string; value?: unknown }) {
    setSelectedDynamicField(data);
    setOpenDynamicFieldSelector(true);
  }

  function onDeleteDynamicField(data: { title: string; dataType: string; value?: unknown }) {
    const current = form.getValues("dynamicFields");
    form.setValue(
      "dynamicFields",
      current.filter((f) => f !== data),
    );
  }

  const loading = isEditing || isSettingTags;
  // Sequential flows render this column set through shadcn DataTable.
  // The hierarchical branch below still consumes BFHierarchyTable's
  // legacy `{name, label, options.customBodyRender}` shape — those
  // get adapted inline when we eventually swap the tree table.
  const sequentialColumns = useMemo<ColumnDef<FlowItem>[]>(
    () => {
      const cols: ColumnDef<FlowItem>[] = [
        {
          accessorKey: "title",
          header: t("items.items.identifier"),
        },
        {
          accessorKey: "description",
          header: t("items.items.description"),
        },
      ];
      if (hasRemoveItemPermission) {
        cols.push({
          id: "remove",
          header: " ",
          enableSorting: false,
          cell: ({ row }) => (
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                onRemoveItem({ value: row.original });
              }}
              aria-label={t("common.delete", "Delete")}
              className="text-destructive hover:bg-destructive/10 hover:text-destructive"
            >
              <Trash2Icon className="size-4" />
            </Button>
          ),
        });
      }
      return cols;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, hasRemoveItemPermission],
  );

  // Legacy column set still consumed by BFHierarchyTable.
  const hierarchicalColumns = useMemo(
    () => {
      const cols: Array<Record<string, unknown>> = [
        { name: "title", label: t("items.items.identifier") },
        { name: "description", label: t("items.items.description") },
      ];
      if (hasRemoveItemPermission) {
        cols.push({
          name: "onDelete",
          label: " ",
          options: {
            customBodyRender: (_val: unknown, tableMeta: unknown) => (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => onRemoveItem(tableMeta as FlowItem)}
                aria-label={t("common.delete", "Delete")}
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
              >
                <Trash2Icon className="size-4" />
              </Button>
            ),
          },
        });
      }
      return cols;
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, hasRemoveItemPermission],
  );

  const currentValues = form.watch();
  const flowTypeIsSequential =
    value?.flowType?.name === FlowTypes.SEQUENTIAL ||
    flowTypesList.find((ft) => String(ft.id) === currentValues.typeId)?.name ===
      FlowTypes.SEQUENTIAL;

  return (
    <>
      <EntitySheet
        open={open}
        onOpenChange={onOpenChange}
        title={value?.name ?? t("sidebar.flows")}
        editing={editing}
        onToggleEdit={setEditing}
        canEdit={canEdit}
        onDelete={onDelete}
        size="lg"
        footer={
          activeTab === "details" && editing ? (
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setEditing(false)} disabled={loading}>
                {t("common.cancel")}
              </Button>
              <Button onClick={handleSave} disabled={loading}>
                {t("common.save")}
              </Button>
            </div>
          ) : activeTab === "items" && canEdit ? (
            <div className="flex justify-end gap-2">
              <Button onClick={handleSaveItems} disabled={isManagingItems}>
                {t("common.save")}
              </Button>
            </div>
          ) : null
        }
      >
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as "details" | "items")}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="details">{t("flows.add-flow-dialog.title")}</TabsTrigger>
            <TabsTrigger value="items">{t("sidebar.inspection-items")}</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="mt-4">
            {editing ? (
              <FlowDetailsEditForm
                form={form}
                flowTypesList={flowTypesList}
                onAddDynamicField={() => setOpenDynamicFieldSelector(true)}
                onEditDynamicField={onEditDynamicField}
                onDeleteDynamicField={onDeleteDynamicField}
                isSignatureFeatureEnabled={isSignatureFeatureEnabled}
                isEvalFormFeatureEnabled={isEvalFormFeatureEnabled}
                onSubmit={handleSave}
                t={t}
              />
            ) : (
              <FlowDetailsReadView
                value={value}
                tags={currentValues.selectedTags}
                signatureTitles={
                  currentValues.hasSignatures
                    ? currentValues.signaturesCount === 1
                      ? [currentValues.signatureOneTitle]
                      : [
                          currentValues.signatureOneTitle,
                          currentValues.signatureTwoTitle,
                        ]
                    : []
                }
                evalFormActive={currentValues.isEvalFormActive}
                dynamicFields={currentValues.dynamicFields}
                t={t}
              />
            )}
          </TabsContent>

          <TabsContent value="items" className="mt-4 space-y-4">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {flowTypeIsSequential
                  ? t("flows.edit-flow-dialog.select-type.sequencial")
                  : t("flows.edit-flow-dialog.select-type.free")}
              </p>
              {hasAddItemPermission ? (
                <Button size="sm" onClick={() => setOpenItemFlowDialog(true)}>
                  <PlusIcon className="size-4" />
                  {t("items.add-item-dialog.title")}
                </Button>
              ) : null}
            </div>
            {items.length > 0 ? (
              flowTypeIsSequential ? (
                <DataTable
                  columns={sequentialColumns}
                  data={items}
                  emptyMessage={t("common.no-data", "No items yet")}
                />
              ) : (
                <BFHierarchyTable
                  id="item"
                  data={{ itemCount: items.length, items: [...items], range: { limit: 15 } }}
                  columns={hierarchicalColumns}
                  leaf="children"
                  page={0}
                  sort={null}
                />
              )
            ) : (
              <p className="text-sm text-muted-foreground">{t("common.no-data", "No items yet")}</p>
            )}
          </TabsContent>
        </Tabs>
      </EntitySheet>

      {/* Sub-modal still MUI; migrates in Phase 6. */}
      <ItemFlowDialog
        flow={value ?? {}}
        previouslySelectedItems={collectAllIds(items)}
        open={openItemFlowDialog}
        onClose={() => setOpenItemFlowDialog(false)}
        onAddItems={onAddItems}
      />

      <DynamicFieldSelectorDialog
        open={openDynamicFieldSelector}
        onClose={() => {
          setSelectedDynamicField(null);
          setOpenDynamicFieldSelector(false);
        }}
        onCreated={onAddDynamicField}
        title={t("components.dynamic-field-selector.title")}
        value={selectedDynamicField}
        titleOnly
      />
    </>
  );
}

// ---------- Helpers ----------

function collectAllIds(items: FlowItem[]): (string | number)[] {
  const out = new Set<string | number>();
  function recur(item: FlowItem) {
    out.add(item.id);
    if (item.children && item.children.length > 0) {
      for (const child of item.children) recur(child);
    }
  }
  for (const it of items) recur(it);
  return Array.from(out);
}

function removeItemById(
  items: FlowItem[],
  leaf: keyof FlowItem,
  id: string | number,
): FlowItem[] {
  return items
    .filter((it) => it.id !== id)
    .map((it) => ({
      ...it,
      [leaf]: it[leaf]
        ? removeItemById(it[leaf] as FlowItem[], leaf, id)
        : [],
    })) as FlowItem[];
}

// ---------- Sub-views ----------

type FlowFormValues = {
  flowTitle: string;
  typeId: string;
  selectedTags: TagRef[];
  hasSignatures: boolean;
  signaturesCount: 1 | 2;
  signatureOneTitle: string;
  signatureTwoTitle: string;
  isEvalFormActive: boolean;
  dynamicFields: Array<{ title: string; dataType: string; value?: unknown }>;
};

function FlowDetailsEditForm({
  form,
  flowTypesList,
  onAddDynamicField,
  onEditDynamicField,
  onDeleteDynamicField,
  isSignatureFeatureEnabled,
  isEvalFormFeatureEnabled,
  onSubmit,
  t,
}: {
  form: UseFormReturn<FlowFormValues>;
  flowTypesList: Array<{ id: string | number; name?: string; title: string }>;
  onAddDynamicField: () => void;
  onEditDynamicField: (data: { title: string; dataType: string; value?: unknown }) => void;
  onDeleteDynamicField: (data: { title: string; dataType: string; value?: unknown }) => void;
  isSignatureFeatureEnabled: boolean;
  isEvalFormFeatureEnabled: boolean;
  onSubmit: (e?: React.BaseSyntheticEvent) => void;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const watchHasSignatures = form.watch("hasSignatures");
  const watchSignaturesCount = form.watch("signaturesCount");
  const watchEvalFormActive = form.watch("isEvalFormActive");
  const watchDynamicFields = form.watch("dynamicFields");

  return (
    <Form {...form}>
      <form onSubmit={onSubmit} className="space-y-5" noValidate>
        <FormField
          control={form.control}
          name="flowTitle"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("flows.edit-flow-dialog.flow-title")}</FormLabel>
              <FormControl>
                <Input {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="typeId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t("flows.edit-flow-dialog.type")}</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder={t("flows.edit-flow-dialog.type")} />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {flowTypesList.map((ft) => (
                    <SelectItem key={ft.id} value={String(ft.id)}>
                      {ft.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="selectedTags"
          render={({ field }) => (
            <FormItem>
              <TagsMultiSelect
                value={field.value}
                onChange={field.onChange}
                label={t("flows.edit-flow-dialog.tags-title")}
                placeholder={t("tags.multi-select.placeholder")}
              />
            </FormItem>
          )}
        />

        {isSignatureFeatureEnabled ? (
          <div className="space-y-3 border-t border-border pt-4">
            <FormField
              control={form.control}
              name="hasSignatures"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(v) => field.onChange(Boolean(v))}
                    />
                  </FormControl>
                  <span className="text-sm">
                    {t("flows.add-flow-dialog.add-signatures")}
                  </span>
                </FormItem>
              )}
            />
            {watchHasSignatures ? (
              <div className="space-y-3 pl-6">
                <FormField
                  control={form.control}
                  name="signaturesCount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("flows.add-flow-dialog.signatures-number")}
                      </FormLabel>
                      <Select
                        value={String(field.value)}
                        onValueChange={(v) => field.onChange(v === "2" ? 2 : 1)}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="1">1</SelectItem>
                          <SelectItem value="2">2</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="signatureOneTitle"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>
                        {t("flows.add-flow-dialog.signature-one-title")}
                      </FormLabel>
                      <FormControl>
                        <Input {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                {watchSignaturesCount === 2 ? (
                  <FormField
                    control={form.control}
                    name="signatureTwoTitle"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>
                          {t("flows.add-flow-dialog.signature-two-title")}
                        </FormLabel>
                        <FormControl>
                          <Input {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}

        {isEvalFormFeatureEnabled ? (
          <div className="space-y-3 border-t border-border pt-4">
            <FormField
              control={form.control}
              name="isEvalFormActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center gap-2 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={(v) => field.onChange(Boolean(v))}
                    />
                  </FormControl>
                  <span className="text-sm">
                    {t("flows.add-flow-dialog.enable-resource-evaluation-form")}
                  </span>
                </FormItem>
              )}
            />
            {watchEvalFormActive ? (
              <div className="space-y-3 pl-6">
                {watchDynamicFields.map((field) => (
                  <DynamicFieldValueComponent
                    key={`${field.title}-${field.dataType}`}
                    value={field}
                    onEdit={onEditDynamicField}
                    onDelete={onDeleteDynamicField}
                    titleOnly
                  />
                ))}
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="text-primary"
                  onClick={onAddDynamicField}
                >
                  <PlusIcon className="size-4" />
                  {t("resource.add-resource-dialog.add-field")}
                </Button>
              </div>
            ) : null}
          </div>
        ) : null}
      </form>
    </Form>
  );
}

function FlowDetailsReadView({
  value,
  tags,
  signatureTitles,
  evalFormActive,
  dynamicFields,
  t,
}: {
  value: FlowRowForSheet | null;
  tags: TagRef[];
  signatureTitles: string[];
  evalFormActive: boolean;
  dynamicFields: Array<{ title: string; dataType: string; value?: unknown }>;
  t: ReturnType<typeof useTranslation>["t"];
}) {
  const typeName = value?.flowType?.name;
  const typeLabel = typeName === FlowTypes.SEQUENTIAL
    ? t("flows.edit-flow-dialog.select-type.sequencial")
    : t("flows.edit-flow-dialog.select-type.free");

  return (
    <div className="space-y-5">
      <section>
        <h3 className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("flows.items.type")}
        </h3>
        <p className="text-sm">{typeLabel}</p>
      </section>

      <section>
        <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t("resource.items.tags")}
        </h3>
        {tags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((tag) => (
              <Badge
                key={tag.id}
                style={{
                  backgroundColor: tag.color ?? undefined,
                  color: tag.color ? "#fff" : undefined,
                }}
              >
                {tag.name}
              </Badge>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">{t("tags.empty")}</p>
        )}
      </section>

      {signatureTitles.length > 0 ? (
        <section>
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("flows.add-flow-dialog.add-signatures")}
          </h3>
          <ul className="space-y-1 text-sm">
            {signatureTitles.map((title, idx) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={idx}>{title || `—`}</li>
            ))}
          </ul>
        </section>
      ) : null}

      {evalFormActive && dynamicFields.length > 0 ? (
        <section>
          <h3 className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {t("flows.add-flow-dialog.enable-resource-evaluation-form")}
          </h3>
          <ul className="space-y-1 text-sm">
            {dynamicFields.map((field) => (
              <li key={`${field.title}-${field.dataType}`}>{field.title}</li>
            ))}
          </ul>
        </section>
      ) : null}
    </div>
  );
}
